import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, ShoppingBag, User, LogOut, Upload, Library, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, BRAND } from "@/config";
import { InstallButton } from "@/components/InstallButton";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const Navbar = ({ search, setSearch }) => {
  const { user, logout, setAuthOpen } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-40 backdrop-blur-2xl bg-[#050505]/80 border-b border-white/10">
      <div className="px-6 md:px-12 h-16 flex items-center gap-4 md:gap-8">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 shrink-0">
          <img src={LOGO_URL} alt={BRAND} className="h-9 w-9 object-contain" />
          <span className="font-heading font-extrabold text-lg md:text-xl tracking-tight leading-none">{BRAND}</span>
        </Link>

        {setSearch && (
          <div className="hidden md:flex items-center gap-2 flex-1 max-w-md bg-[#121212] border border-[#2a2a2a] rounded-full px-4 py-2 focus-within:border-[#e2ff31] transition-colors">
            <Search className="w-4 h-4 text-[#6b7280]" />
            <input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search beats, producers, genres..."
              className="bg-transparent outline-none text-sm w-full placeholder:text-[#6b7280]"
            />
          </div>
        )}

        <div className="flex items-center gap-2 md:gap-3 ml-auto">
          <InstallButton />
          {user === false && (
            <Button
              data-testid="navbar-signin-btn"
              onClick={() => setAuthOpen(true)}
              className="rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold"
            >
              Sign in
            </Button>
          )}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="flex items-center gap-2 rounded-full bg-[#121212] border border-[#2a2a2a] hover:bg-[#1e1e1e] transition-colors pl-3 pr-1.5 py-1.5">
                  <span className="text-sm hidden sm:block max-w-[120px] truncate">{user.name}</span>
                  <span className="w-7 h-7 rounded-full bg-[#e2ff31] text-black flex items-center justify-center font-bold text-sm">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#121212] border-[#2a2a2a] text-[#f3f4f6] w-52">
                <div className="px-2 py-1.5 text-xs text-[#6b7280] truncate">{user.email}</div>
                <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                <DropdownMenuItem data-testid="menu-library" onClick={() => navigate("/purchases")} className="cursor-pointer focus:bg-[#1e1e1e]">
                  <Library className="w-4 h-4 mr-2" /> My Purchases
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-wishlist" onClick={() => navigate("/wishlist")} className="cursor-pointer focus:bg-[#1e1e1e]">
                  <Heart className="w-4 h-4 mr-2" /> My Wishlist
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-[#1e1e1e]">
                    <Upload className="w-4 h-4 mr-2" /> Manage Beats
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer focus:bg-[#1e1e1e] text-[#ef4444]">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </nav>
  );
};
